import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { User, Company } from '@lebusiness/types';

// Transform database snake_case to TypeScript camelCase
function transformUserProfile(raw: any): User {
  return {
    id: raw.id,
    email: raw.email,
    fullName: raw.full_name,
    role: raw.role,
    isSuperAdmin: raw.is_super_admin ?? false,
    companyId: raw.company_id,
    isActive: raw.is_active,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface AuthState {
  user: SupabaseUser | null;
  profile: User | null;
  company: Company | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      company: null,
      isLoading: false,
      isAuthenticated: false,

      signIn: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error) throw error;

          // Fetch user profile
          const { data: rawProfile, error: profileError } = await supabase
            .from('users')
            .select('*')
            .eq('id', data.user.id)
            .single();

          if (profileError) throw profileError;

          console.log('Raw profile data:', rawProfile);
          // Transform snake_case to camelCase
          const profile = transformUserProfile(rawProfile);

          console.log('Transformed profile data:', profile);
          // Fetch company separately
          let company = null;
          if (rawProfile?.company_id) {
            const { data: companyData } = await supabase
              .from('companies')
              .select('*')
              .eq('id', rawProfile.company_id)
              .single();
            company = companyData;
          }

          set({
            user: data.user,
            profile,
            company: company as Company,
            isAuthenticated: true,
          });
        } catch (error) {
          console.error('Sign in error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      signUp: async (email: string, password: string, fullName: string) => {
        set({ isLoading: true });
        try {
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: fullName,
              },
            },
          });

          if (error) throw error;

          // Profile will be created by database trigger
          set({
            user: data.user,
            isAuthenticated: false, // Wait for email verification
          });
        } catch (error) {
          console.error('Sign up error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      signOut: async () => {
        set({ isLoading: true });
        try {
          const { error } = await supabase.auth.signOut();
          if (error) throw error;

          set({
            user: null,
            profile: null,
            company: null,
            isAuthenticated: false,
          });
        } catch (error) {
          console.error('Sign out error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      initialize: async () => {
        set({ isLoading: true });
        try {
          const { data: { session } } = await supabase.auth.getSession();

          if (session?.user) {
            // Fetch user profile
            const { data: rawProfile, error: profileError } = await supabase
              .from('users')
              .select('*')
              .eq('id', session.user.id)
              .single();

            if (profileError) {
              console.error('Profile fetch error:', profileError);
              set({ isLoading: false });
              return;
            }

            // Transform snake_case to camelCase
            const profile = transformUserProfile(rawProfile);

            // Fetch company separately
            let company = null;
            if (rawProfile?.company_id) {
              const { data: companyData, error: companyError } = await supabase
                .from('companies')
                .select('*')
                .eq('id', rawProfile.company_id)
                .single();

              if (!companyError) {
                company = companyData;
              }
            }

            set({
              user: session.user,
              profile,
              company: company as Company,
              isAuthenticated: true,
            });
          }

          // Listen for auth changes
          supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === 'SIGNED_IN' && session?.user) {
              const { data: rawProfile } = await supabase
                .from('users')
                .select('*')
                .eq('id', session.user.id)
                .single();

              if (rawProfile) {
                // Transform snake_case to camelCase
                const profile = transformUserProfile(rawProfile);

                let company = null;
                if (rawProfile?.company_id) {
                  const { data: companyData } = await supabase
                    .from('companies')
                    .select('*')
                    .eq('id', rawProfile.company_id)
                    .single();
                  company = companyData;
                }

                set({
                  user: session.user,
                  profile,
                  company: company as Company,
                  isAuthenticated: true,
                });
              }
            } else if (event === 'SIGNED_OUT') {
              set({
                user: null,
                profile: null,
                company: null,
                isAuthenticated: false,
              });
            }
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        profile: state.profile,
        company: state.company,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
